import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import { db, firebase } from "@/firebase/firebaseConfig.js";
import moment from "moment-timezone";
import { COL_NAME } from "@/config/const.js";
import isNumber from "lodash/isNumber";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import isDate from "lodash/isDate";
import isEmpty from "lodash/isEmpty";
import mapKeys from "lodash/mapKeys";
import merge from "lodash/merge";
import FireStoreService from "@/services/fireStoreService";
import { isSameTaskVesselNameAndMasterVesselName } from "@/utils/gkmgPortMaster";

const vesselApi = AiseaApiFactory.get("vessel");
const gkmgPortMasterTasksApi = AiseaApiFactory.get("gkmgPortMasterTasks");

export default {
  /**
   * ORDER_LIST_DATE: {
   *  BEFORE_DAYS: 2, // 何日前
   *  AFTER_DAYS: 2, // 何日後
   * }
   *
   * @param {*} payload
   */
  async setTargetDates({ commit, dispatch }, payload) {
    await dispatch("fetchMastersFromFirestore");
    const dateList = [];
    for (let i = -1 * payload.BEFORE_DAYS; i <= payload.AFTER_DAYS; i++) {
      dateList.push(moment(new Date()).add(i, "days").format("YYYY/MM/DD"));
    }
    await commit("SET_TARGET_DATES", dateList);
  },

  /**
   * Firestoreの入出港タスク情報の変更を検知したときの処理.
   *
   * @param {*} payload
   */
  async subscribeDestinationWorkTasks({ commit, state, dispatch }, payload) {
    /* payload : gkmg_pm_vesselsから取得したマスタデータ。
     * gkmg_pm_tasksから取得した船舶IDとgkmg_pm_vessels内の船舶IDを紐づけて、
     * gkmg_pm_vesselsから船舶名、船舶種別等の情報を取得するために使用する。
     */

    const subscribeObj = await db
      .collection("gkmg_pm_tasks")
      .where(
        "order_time",
        ">=",
        firebase.firestore.Timestamp.fromDate(
          moment(state.displayDates[0]).toDate(),
        ),
      )
      .where(
        "order_time",
        "<",
        firebase.firestore.Timestamp.fromDate(
          moment(state.displayDates[state.displayDates.length - 1])
            .add(1, "day")
            .toDate(),
        ),
      )
      .onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((diff) => {
          if (!diff.doc.data().order_time) return;
          const displayDate =
            diff.doc.data().order_time && diff.doc.data().order_time.seconds
              ? moment(diff.doc.data().order_time.seconds * 1000)
                  .local()
                  .format("YYYYMMDD")
              : null;
          const vesselsMaster = diff.doc.data().vessel_id
            ? payload.find(
                (el) =>
                  String(el.data.id) === String(diff.doc.data().vessel_id),
              )
            : null;

          if (diff.type === "added") {
            let taskData = {
              date: displayDate,
              taskId: diff.doc.id,
              docData: diff.doc.data(),
              vesselsMaster: vesselsMaster,
            };
            dispatch("saveTaskWithName", taskData);
          } else if (diff.type === "modified") {
            let taskData = {
              date: displayDate,
              taskId: diff.doc.id,
              docData: diff.doc.data(),
              vesselsMaster: vesselsMaster,
            };
            // タスクのデータが変更された場合、 destinationWorkTasksMap から元のデータを削除してから新しいデータを追加する
            for (const [key, destinationWorkTasks] of Object.entries(
              state.destinationWorkTasksMap,
            )) {
              if (destinationWorkTasks[diff.doc.id]) {
                commit("DELETE_DESTINATION_WORK_TASK", {
                  date: key,
                  taskId: diff.doc.id,
                }); // 削除
                break;
              }
            }
            dispatch("saveTaskWithName", taskData); // 追加
          } else {
            // added, modified でない場合: removed
            let taskData = {
              date: displayDate,
              taskId: diff.doc.id,
            };
            commit("DELETE_DESTINATION_WORK_TASK", taskData);
            commit(
              "SET_LAST_UPDATED_FOR_TASKS",
              Math.floor(moment().valueOf() / 1000),
            );
          }

          // gkmg_pm_tasksのupdated_atを取得し格納
          if (diff.doc.data() && diff.doc.data().updated_at) {
            commit("SET_LAST_UPDATED_TIME_MAP", {
              date: displayDate,
              time: (state.lastUpdatedTimeMap[displayDate] || []).concat([
                diff.doc.data().updated_at.seconds,
              ]),
            });
          }
        });
      });
    commit("ADD_FIRESTORE_SUBSCRIBE_LIST", subscribeObj);
  },

  /**
   * Firestoreの入出港タスク情報(=idのみのデータ)にマスタ情報を使って名称などを付与.
   *
   * @param {*} payload
   * payloadはオブジェクト型で、以下4つのキーバリューのセットを持つ。
   *  1) date         : 文字列    ex)20230621
   *  2) taskId       : 文字列    ※ gkmg_pm_tasksのドキュメントID
   *  3) docData      : {}       ex){ berth_id:2, port_id:1,....}といった、入出港予定一覧表に表示する列項目とその値
   *  4) vesselsMaster: {}       ex)vesselsMasterは以下
   *
   * vesselsMasterは以下3つのキーバリューのセットを持つ。
   *  1) changeType   : 文字列    ex)added,removed、firestoreでデータが更新された際に与えられる
   *  2) data         : {}       ex){name: 船の名前, type: 船の種類,.....}といった、gkmg_pm_vessels.<ドキュメントID>内の各データ
   *  3) id           : 文字列    ※ gkmg_pm_vessels直下のドキュメントID
   */
  attachNameToDocData({ state }, payload) {
    // 整形＋マスタ名を足す処理を追加
    const vesselMaster = payload.vesselsMaster
      ? payload.vesselsMaster.data
      : null;

    const entriedIslandTree = Object.entries(state.masterMap.island_tree).map(
      (el) => Object.entries(el),
    );

    const docData = payload.docData;
    const { changes } = docData;

    const hasIslandItems = checkIslandItems(docData, state.masterMap);
    const islandId = hasIslandItems
      ? Number(
          entriedIslandTree
            .find((el) =>
              Object.keys(el[1][1]).includes(`port:${docData.port_id}`),
            )[0][1]
            .split(":")[1],
        )
      : null;
    const portId = hasIslandItems ? docData.port_id : null;
    const quayId = hasIslandItems ? docData.quay_id : null;
    const berthId = hasIslandItems ? docData.berth_id : null;

    const hasFromAnchorageTime =
      Object.hasOwn(docData, "from_anchorage_time") &&
      FireStoreService.isInstanceOfFirebaseFirestoreTimestamp(
        docData.from_anchorage_time,
      );
    const hasToAnchorageTime =
      Object.hasOwn(docData, "to_anchorage_time") &&
      FireStoreService.isInstanceOfFirebaseFirestoreTimestamp(
        docData.to_anchorage_time,
      );
    const hasFromDate =
      Object.hasOwn(docData, "from_date") &&
      FireStoreService.isInstanceOfFirebaseFirestoreTimestamp(
        docData.from_date,
      );
    const hasToDate =
      Object.hasOwn(docData, "to_date") &&
      FireStoreService.isInstanceOfFirebaseFirestoreTimestamp(docData.to_date);

    // 一覧表に表示する船舶名を取得
    // taskのvessel_nameがあればそれを使う
    // なければ、vessel_masterのnameを使う
    const calcVesselName = () => {
      const hasTaskVesselName =
        Object.hasOwn(docData, "vessel_name") &&
        isString(docData.vessel_name) &&
        docData.vessel_name.length > 0;
      const hasVesselMasterVesselName =
        isObject(vesselMaster) &&
        Object.hasOwn(vesselMaster, "name") &&
        isString(vesselMaster.name) &&
        vesselMaster.name.length > 0;
      if (hasTaskVesselName) return docData.vessel_name;
      if (hasVesselMasterVesselName) return vesselMaster.name;
      return null;
    };

    const isSameVesselMaster = isSameTaskVesselNameAndMasterVesselName(
      payload,
      payload.vesselsMaster,
    );
    const getVesselMasterProperty = (
      property, // string
    ) => {
      if (!isSameVesselMaster) {
        return "";
      }
      const propertyValue =
        vesselMaster && Object.hasOwn(vesselMaster, property)
          ? vesselMaster[property]
          : null;
      return propertyValue;
    };
    // 一覧に表示する会社名を取得
    // taskとvessel_masterのvessel_nameが一致している場合のみ、vessel_masterのcompany_nameを使う
    const calcCompanyName = () => {
      return getVesselMasterProperty("company_name");
    };

    // 一覧に表示する注意事項を取得
    // taskとvessel_masterのvessel_nameが一致している場合のみ、vessel_masterのcautionary_noteを使う
    const calcCautionaryNote = () => {
      return getVesselMasterProperty("cautionary_note");
    };

    let taskDataWithName = {
      docData: docData, // マスタ名更新などで、再展開するために、元データは保持しておく。
      taskId: {
        valueMap: {
          itSelf: payload.taskId ? payload.taskId : null,
        },
      },
      sort_order: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "sort_order")
            ? docData.sort_order
            : null,
          order_time_string: docData.order_time
            ? moment(docData.order_time.seconds * 1000)
                .local()
                .format("HH:mm")
            : "--:--",
          displayType: COL_NAME.find((el) => el.columnKey === "sort_order")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "sort_order")
            .editable,
          changes: changes && changes.sort_order ? changes.sort_order : {},
        },
      },
      highlight: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "highlight")
            ? docData.highlight
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "highlight")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "highlight")
            .editable,
          changes: changes && changes.highlight ? changes.highlight : null,
        },
      },
      progress: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "progress") ? docData.progress : null,
          progress_time: Object.hasOwn(docData, "progress_time")
            ? docData.progress_time
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "progress")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "progress").editable,
          changes: changes && changes.progress ? changes.progress : null,
        },
      },
      order_time: {
        valueMap: {
          itSelf:
            Object.hasOwn(docData, "order_time") &&
            Object.hasOwn(docData.order_time, "seconds")
              ? docData.order_time.seconds
              : null,
          displayType: COL_NAME.find((el) => el.columnKey === "order_time")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "order_time")
            .editable,
          changes: changes && changes.order_time ? changes.order_time : null,
        },
      },
      vessel_id: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "vessel_id")
            ? docData.vessel_id
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "vessel_id")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "vessel_id")
            .editable,
          changes: changes && changes.vessel_id ? changes.vessel_id : null,
        },
      },
      from_anchorage_time: {
        valueMap: {
          itSelf: hasFromAnchorageTime
            ? docData.from_anchorage_time.seconds
            : null,
          displayType: COL_NAME.find(
            (el) => el.columnKey === "from_anchorage_time",
          ).displayType,
          editable: COL_NAME.find(
            (el) => el.columnKey === "from_anchorage_time",
          ).editable,
          changes:
            changes && changes.from_anchorage_time
              ? changes.from_anchorage_time
              : null,
        },
      },
      to_anchorage_time: {
        valueMap: {
          itSelf: hasToAnchorageTime ? docData.to_anchorage_time.seconds : null,
          displayType: COL_NAME.find(
            (el) => el.columnKey === "to_anchorage_time",
          ).displayType,
          editable: COL_NAME.find((el) => el.columnKey === "to_anchorage_time")
            .editable,
          changes:
            changes && changes.to_anchorage_time
              ? changes.to_anchorage_time
              : null,
        },
      },
      from_date: {
        valueMap: {
          itSelf: hasFromDate ? docData.from_date.seconds : null,
          displayType: COL_NAME.find((el) => el.columnKey === "from_date")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "from_date")
            .editable,
          changes: changes && changes.from_data ? changes.from_data : null,
        },
      },
      to_date: {
        valueMap: {
          itSelf: hasToDate ? docData.to_date.seconds : null,
          displayType: COL_NAME.find((el) => el.columnKey === "to_date")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "to_date").editable,
          changes: changes && changes.to_data ? changes.to_data : null,
        },
      },
      island_id: {
        valueMap: {
          itSelf: islandId,
          islandId,
          portId,
          quayId,
          berthId,
          displayType: COL_NAME.find((el) => el.columnKey === "island_id")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "island_id")
            .editable,
          changes: changes && changes.island_id ? changes.island_id : null,
        },
      },
      port_id: {
        valueMap: {
          itSelf: portId,
          islandId,
          portId,
          quayId,
          berthId,
          displayType: COL_NAME.find((el) => el.columnKey === "port_id")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "port_id").editable,
          changes:
            docData.changes && docData.changes.port_id ? changes.port_id : null,
        },
      },
      quay_id: {
        valueMap: {
          itSelf: quayId,
          islandId,
          portId,
          quayId,
          berthId,
          displayType: COL_NAME.find((el) => el.columnKey === "quay_id")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "quay_id").editable,
          changes:
            docData.changes && docData.changes.quay_id
              ? docData.changes.quay_id
              : null,
        },
      },
      berth_id: {
        valueMap: {
          itSelf: berthId,
          islandId,
          portId,
          quayId,
          berthId,
          displayType: COL_NAME.find((el) => el.columnKey === "berth_id")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "berth_id").editable,
          changes:
            docData.changes && docData.changes.berth_id
              ? docData.changes.berth_id
              : null,
        },
      },

      /** フロントから入力する項目で、gkmg_pm_tasksに保存するもの */
      team_leader: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "team_leader")
            ? docData.team_leader
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "team_leader")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "team_leader")
            .editable,
          changes:
            docData.changes && docData.changes.team_leader
              ? docData.changes.team_leader
              : null,
        },
      },
      vehicles: {
        valueMap: {
          itSelf:
            Object.hasOwn(docData, "vehicles") && docData.vehicles !== null
              ? docData.vehicles
              : [],
          displayType: COL_NAME.find((el) => el.columnKey === "vehicles")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "vehicles").editable,
          changes:
            docData.changes && docData.changes.vehicles
              ? docData.changes.vehicles
              : null,
        },
      },
      flag: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "flag") ? docData.flag : null,
          displayType: COL_NAME.find((el) => el.columnKey === "flag")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "flag").editable,
          changes:
            docData.changes && docData.changes.flag
              ? docData.changes.flag
              : null,
        },
      },
      bit: {
        valueMap: {
          itSelf:
            Object.hasOwn(docData, "bit#left") ||
            Object.hasOwn(docData, "bit#right")
              ? `${docData["bit#left"]} - ${docData["bit#right"]}`
              : null,
          left: Object.hasOwn(docData, "bit#left") ? docData["bit#left"] : null,
          right: Object.hasOwn(docData, "bit#right")
            ? docData["bit#right"]
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "bit")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "bit").editable,
          changes: changes && changes.bit ? changes.bit : null,
        },
      },
      boardside_type: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "boardside_type")
            ? docData["boardside_type"]
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "boardside_type")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "boardside_type")
            .editable,
          changes:
            docData.changes && docData.changes.boardside_type
              ? docData.changes.boardside_type
              : null,
        },
      },
      required_note: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "required_note")
            ? docData.required_note
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "required_note")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "required_note")
            .editable,
          changes:
            docData.changes && docData.changes.required_note
              ? docData.changes.required_note
              : null,
        },
      },
      /*
       * loading_weight,boarding_numberはgkmg_pm_tasksとgkmg_pm_vesselsに保存している。vesselsに保存しているのはマスタに紐づく静的な値だが、
       * gkmg_pm_tasksにはそれとは別に動的な値を保存する。入出港予定一覧表で表示するのは、gkmg_pm_tasksの値のみで、gkmg_pm_vesselsの値は表示しない。
       */
      loading_weight: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "loading_weight")
            ? docData["loading_weight"]
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "loading_weight")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "loading_weight")
            .editable,
          changes:
            docData.changes && docData.changes.loading_weight
              ? docData.changes.loading_weight
              : null,
        },
      },
      boarding_number: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "boarding_number")
            ? docData["boarding_number"]
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "boarding_number")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "boarding_number")
            .editable,
          changes:
            docData.changes && docData.changes.boarding_number
              ? docData.changes.boarding_number
              : null,
        },
      },
      /*
       * 必要人数(head_count)は、gkmg_pm_tasksに値があればその値を、
       * なければgkmg_pm_vesselsのデフォルト値を、入出港予定一覧表に表示させる。
       */
      head_count: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "head_count")
            ? docData.head_count
            : vesselMaster && Object.hasOwn(vesselMaster, "head_count")
            ? vesselMaster.head_count
            : null,
          displayType: COL_NAME.find((el) => el.columnKey === "head_count")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "head_count")
            .editable,
        },
      },

      /*
       * csv アップロードで、gkmg_pm_vesselsに格納するもの。
       */
      name: {
        valueMap: {
          itSelf: calcVesselName(),
          displayType: COL_NAME.find((el) => el.columnKey === "name")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "name").editable,
        },
      },
      company_name: {
        valueMap: {
          itSelf: calcCompanyName(),
          displayType: COL_NAME.find((el) => el.columnKey === "company_name")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "company_name")
            .editable,
        },
      },

      cautionary_note: {
        valueMap: {
          itSelf: calcCautionaryNote(),
          displayType: COL_NAME.find((el) => el.columnKey === "cautionary_note")
            .displayType,
          editable: COL_NAME.find((el) => el.columnKey === "cautionary_note")
            .editable,
        },
      },

      // 目的地
      destination_name: {
        valueMap: {
          itSelf: Object.hasOwn(docData, "destination_name")
            ? docData.destination_name
            : null,
          displayType: COL_NAME.find(
            (el) => el.columnKey === "destination_name",
          ).displayType,
          editable: COL_NAME.find((el) => el.columnKey === "destination_name")
            .editable,
          changes:
            docData.changes && docData.changes.destination_name
              ? docData.changes.destination_name
              : null,
        },
      },
    };

    for (let key of Object.keys(taskDataWithName)) {
      if (key !== "docData") {
        taskDataWithName[key]["taskId"] = payload.taskId;
        taskDataWithName[key]["columnKey"] = key;
        taskDataWithName[key]["changes"] =
          changes && changes[key] ? changes[key].seconds * 1000 : null;
        taskDataWithName[key]["highlight"] = docData["highlight"];
      }
    }

    return taskDataWithName;
  },
  async saveTaskWithName({ dispatch, commit }, payload) {
    const destinationWorkTask = await dispatch("attachNameToDocData", payload);

    // steteに保存.
    // 保存＆呼び出しを速くするため、state.destinationWorkTasksMap[payload.date][payload.taskId] の連想配列型で保存.
    let taskData = {
      date: payload.date,
      taskId: payload.taskId,
      destinationWorkTask: destinationWorkTask,
    };
    commit("SET_DESTINATION_WORK_TASK", taskData);
    commit("SET_LAST_UPDATED_FOR_TASKS", Math.floor(moment().valueOf() / 1000));
  },

  /**
   * Firestore ディスプレイ設定を取得し、stateに保存.
   *
   * @param {*} payload
   */
  async fetchMastersFromFirestore({ commit }) {
    // "main_data"という名前でドキュメントが作成されている。
    const collectionRef = db.collection("gkmg_pm_settings").doc("main_data");
    const doc = await collectionRef.get();
    const masterData = !doc.exists ? null : doc.data();

    Object.keys(masterData).map((key) => {
      //commit("SET_MASTER_MAP",masterData[key]);
      commit("SET_MASTER_MAP", {
        masterName: String(key),
        masterList: masterData[key],
      });
    });
  },

  /**
   * 船舶情報を取得する
   * @param {any} _ 未使用のパラメータ
   * @param {Object} payload ペイロードオブジェクト
   * @param {string} payload.vesselId aisea's 船舶ID
   * @returns {Promise<Object|null|Error>} 船舶情報のオブジェクト、またはnull、エラーオブジェクトをプロミスで返す
   */
  async getVessel(_, payload) {
    return await vesselApi
      .getVessel(payload.vesselId)
      .then((res) => {
        if (!res || !res.data) return null;
        return res.data;
      })
      .catch((err) => {
        return err;
      });
  },

  /**
   * 更新id値から更新後の入出港のタスク情報を生成する。
   *
   * @param {*} updateData
   */
  async mergeUpdateDataIntoTaskRecord({ state, dispatch }, payload) {
    /* stateからタスク1行の生データを取得
     * selectedDateは表示する日付で、YYYY/MM/DDで保持している。
     * 一方destinationWorkTasksMapのキーはYYYYMMDD形式となっている
     */
    const { updateData, vesselsData } = payload;
    let stateTaskDocData =
      state.destinationWorkTasksMap[state.selectedDate.replaceAll("/", "")][
        updateData.taskId
      ].docData;

    // changesを生成

    const changeTimestamp = {};
    const currentTimeValue = moment().valueOf();
    changeTimestamp.changes = {};

    if (updateData.updateTimeStamp) {
      for (let key of Object.keys(updateData.data)) {
        if (key !== "changes" && key !== "highlight") {
          const columnKey = key.replace(/#.*$/i, "");
          changeTimestamp.changes[columnKey] = {
            seconds: Math.floor(currentTimeValue / 1000),
            nanoseconds: 0,
          };
        }
      }
    }

    // updateDataをマージして、新しいタスク行を生成。
    const mergedChanges = Object.assign(
      {},
      stateTaskDocData.changes,
      changeTimestamp.changes,
    );
    const newStateTaskDocData = Object.assign(
      {},
      stateTaskDocData,
      updateData.data,
      { changes: mergedChanges },
    );

    // 日付データは、Date型 → FireStoreTimestamp型に変換してから、新しいタスク行に再上書き。
    for (let columnKey of Object.keys(updateData.data)) {
      if (columnKey === "order_time") {
        newStateTaskDocData[columnKey] = !updateData.data[columnKey]
          ? null
          : {
              seconds: Math.floor(updateData.data[columnKey].getTime() / 1000),
              nanoseconds: 0,
            };
      } else if (columnKey === "progress_at") {
        const progress = String(newStateTaskDocData["progress"]);
        if (!newStateTaskDocData[columnKey]) {
          newStateTaskDocData[columnKey] = {};
        }
        newStateTaskDocData[columnKey][progress] =
          !updateData.data[columnKey] || !updateData.data[columnKey][progress]
            ? null
            : {
                seconds: Math.floor(
                  updateData.data[columnKey][progress].getTime() / 1000,
                ),
                nanoseconds: 0,
              };
      }
    }
    // idだけのデータにマスタデータの名前を付与。
    let newTaskData = {
      date: state.selectedDate.replaceAll("/", ""),
      taskId: updateData.taskId,
      docData: newStateTaskDocData,
      vesselsMaster: newStateTaskDocData.vessel_id
        ? vesselsData.find(
            (el) =>
              String(el.data.id) === String(newStateTaskDocData.vessel_id),
          )
        : null,
    };
    const newTaskWithName = await dispatch(
      "attachNameToDocData",
      newTaskData,
      payload.vesselsData,
    );

    return newTaskWithName;
  },

  /**
   * Firestorの入出港タスク情報を更新
   *
   * @param {*} payload
   */
  async updateFirestoreDocument({ state, dispatch, commit }, payload) {
    const updateMode =
      payload.updateMode !== undefined ? payload.updateMode : false;
    const updateTimeStamp =
      payload.updateTimeStamp !== undefined ? payload.updateTimeStamp : false;
    const changeTimestamp = {};
    changeTimestamp.changes = {};
    for (let key of Object.keys(payload.data)) {
      if (key !== "changes") {
        const columnKey = key.replace(/#.*$/i, "");
        changeTimestamp.changes[columnKey] = updateTimeStamp
          ? new Date(moment().toISOString())
          : firebase.firestore.FieldValue.delete();
      }
    }

    const diaplayYmd = state.selectedDate.replaceAll("/", "");
    const orderAtYmd = payload.data["order_time"]
      ? await dispatch("getOrderYmd", {
          targetYmd: diaplayYmd,
          newDate: payload.data["order_time"],
        })
      : null;

    // オーダー時間を変更しない場合か、変更しても同じ日の場合
    if (!orderAtYmd || diaplayYmd === orderAtYmd) {
      const docRef = db.collection("gkmg_pm_tasks").doc(payload.taskId);

      if (
        payload.data["highlight"] !== undefined ||
        payload.data["sort_order"] !== undefined
      ) {
        await docRef.set(payload.data, { merge: true });
      } else if (updateMode) {
        await docRef.update(payload.data);
        await docRef.set(changeTimestamp, { merge: true });
      } else {
        await docRef.set(Object.assign({}, payload.data, changeTimestamp), {
          merge: true,
        });
      }
    } else {
      // オーダー時間の変更が表示日付を超えた場合。
      const docRef = db.collection("gkmg_pm_tasks").doc(payload.taskId);
      const nextPayloadData = {
        ...payload.data,
        sort_order: null, // オーダー時間の変更が表示日付を超えた場合、sort_orderはnullにする
      };

      if (nextPayloadData["highlight"] !== undefined) {
        await docRef.set(nextPayloadData, { merge: true });
      } else if (updateMode) {
        await docRef.update(nextPayloadData);
        await docRef.set(changeTimestamp, { merge: true });
      } else {
        await docRef.set(Object.assign({}, nextPayloadData, changeTimestamp), {
          merge: true,
        });
      }
      if (state.destinationWorkTasksMap[diaplayYmd][payload.taskId]) {
        commit("DELETE_DESTINATION_WORK_TASK", {
          date: diaplayYmd,
          taskId: payload.taskId,
        });
      }
    }
  },

  /**
   * firestoreからdestinationWorkTaskの該当のタスクを消す
   *
   * @param {Object} _ - Vuexストアのコンテキストオブジェクト。
   * @param {Object} payload - ペイロードオブジェクト。
   * @param {string} payload.id - 削除するタスクのID。
   * @returns {Promise<void>} タスクが正常に削除されたときに解決するプロミス。
   * @throws {Error} タスクの削除に失敗したときに発生するエラー。
   */
  async deleteDestinationWorkOneTask(
    _,
    payload, // {id: string;}
  ) {
    const taskId = payload.id;
    const isConnected = await FireStoreService.getIsConnectedToGkmgPmTasks(
      taskId,
    );
    if (!isConnected) {
      throw new Error("タスクの削除に失敗しました。");
    }
    await FireStoreService.deleteGkmgPmTask(payload); // 削除を実行
  },

  /**
   * DestinationWorkタスクを作成します。
   *
   * @param {Object} _ - Vuexストアのコンテキストオブジェクト。
   * @param {Object} payload - タスクの作成に必要な情報を含むオブジェクト。
   * @param {Date} payload.order_time - 注文日時
   * @param {string} payload.vessel_name - 船舶名
   * @param {string|undefined} payload.vessel_id - 船舶ID
   * @param {number} payload.progress - 進捗
   * @throws {Error} タスクの作成に失敗したときに発生するエラー。
   * @returns {Promise<void>} Promiseオブジェクト。
   */
  async createDestinationWorkTask(
    { getters: { progressMaster } },
    payload, // {order_time: Date; vessel_id: string; vessel_name: string; progress: number}
    //        | {order_time: Date; vessel_id: null; vessel_name: string; progress: number}
  ) {
    const isValid = (payload, progressMaster) => {
      if (!isObject(payload)) return false;
      const { order_time, vessel_name, vessel_id, progress } = payload;
      const progressSetting = progressMaster[progress];
      const isValidProgress =
        isNumber(progress) &&
        isString(progressSetting) &&
        progressSetting.length > 0;
      if (!isDate(order_time)) return false; // order_time は必須
      if (!isValidProgress) return false; // progress は必須
      if (!isEmpty(vessel_id)) return !isEmpty(vessel_name); // vessel_id がある場合、vessel_name も必須
      return !(isEmpty(vessel_id) && isEmpty(vessel_name)); // vessel_id と vessel_name どちらもないのはNG
    };
    const isConnected = await FireStoreService.getIsConnectedToFirestore(
      "gkmg_pm_settings",
      "main_data",
    ); // Firestoreに接続できるか確認

    if (!isValid(payload, progressMaster) || !isConnected) {
      throw new Error("タスクの作成に失敗しました。");
    }
    return await FireStoreService.createGkmgPmTask(payload); // 新規作成を実行
  },

  /**
   * 修正後のオーダー時間から、表示対象日を算出する。（tasks.hour_intervalを考慮）
   *
   * payload.targetYmd: 表示対象日（文字列型：YYYYMMDD）
   * payload.newDate: 修正後オーダー日時（Date型）
   *
   */
  getOrderYmd(_, payload) {
    const hourInterval = "00:00";
    if (payload.targetYmd && payload.newDate) {
      let hourIntervalTimeStr = `${payload.targetYmd.slice(
        0,
        4,
      )}-${payload.targetYmd.slice(4, 6)}-${payload.targetYmd.slice(
        6,
        8,
      )}T${hourInterval}:00`;
      for (let i = -30; i < 30; i++) {
        if (
          moment(payload.newDate).valueOf() >=
            moment(hourIntervalTimeStr).add(i, "days").valueOf() &&
          moment(payload.newDate).valueOf() <
            moment(hourIntervalTimeStr)
              .add(i + 1, "days")
              .valueOf()
        ) {
          return moment(hourIntervalTimeStr).add(i, "days").format("YYYYMMDD");
        }
      }
    }
    return null;
  },

  setSelectedCell({ commit }, payload) {
    commit("SET_SELECTED_CELL", payload);
  },

  setSummaryDialogShowPopup({ commit }, payload) {
    commit("SET_SUMMARY_DIALOG_SHOW_POPUP", payload);
  },

  // unsubscribe
  async unsubscribeFirestore({ commit }) {
    commit("UNSUBSCRIBE_FIRESTORE");
  },

  /**
   * 表示する日付を更新する
   * @param {*} param0
   * @param {*} payload
   */
  setSelectedDate({ commit }, payload) {
    commit("SET_SELECTED_DATE", payload);
  },
  /**
   * 入出港予定一覧表で表示する行のタスクIDを更新する
   * @param {*} param0
   * @param {*} payload
   */
  setStoredFilterRule({ commit }, payload) {
    commit("SET_STORED_FILTER_RULE", payload);
  },
  /**
   * 入出港予定一覧表で表示する行のタスクIDを更新する
   * 現在のstoredFilterRuleを元に、payloadをマージする
   *
   * @param {*} payload マージするフィルタの設定
   */
  updateStoredFilterRule(
    { dispatch, state },
    payload, // {
    //     "port_id": {
    //        [key: `${port_id}`]: true;
    //     }
    //   }}
  ) {
    const currentFilterRule = state.storedFilterRule;
    const nextFilterRule = merge({}, currentFilterRule, payload); // deep merge
    dispatch("setStoredFilterRule", nextFilterRule);
  },
  /**
   * 入出港予定一覧表で表示する行のタスクIDを初期化
   */
  resetStoredFilterRule({ commit }) {
    commit("SET_STORED_FILTER_RULE", {});
  },
  /**
   * フィルター中のカラム名を更新する
   */
  setPresentColumnsFilteringState({ commit }, payload) {
    commit("SET_PRESENT_COLUMNS_FILTERING_STATE", payload);
  },
  /**
   * フィルター中のカラム名を初期化
   */
  resetPresentColumnsFilteringState({ commit }) {
    commit("SET_PRESENT_COLUMNS_FILTERING_STATE", {});
  },

  // ファイルアップロード機能
  async importTask(_, payload) {
    const res = await gkmgPortMasterTasksApi.import(payload.file);
    return res;
  },
};

//
// helper functions
//
// port/quay/berthの値が有効かどうかをチェック
const checkIslandItems = (docData, masterMap) => {
  const portMap = mapKeys(masterMap.ports, "id");
  const quayMap = mapKeys(masterMap.quays, "id");
  const checkPortId = (docData) => {
    return (
      Object.hasOwn(docData, "port_id") &&
      isNumber(docData.port_id) &&
      portMap[docData.port_id] !== undefined
    );
  };

  const checkQuayId = (docData) => {
    return (
      Object.hasOwn(docData, "quay_id") &&
      isNumber(docData.quay_id) &&
      isObject(quayMap[docData.quay_id])
    );
  };

  const checkBerthId = (docData) => {
    if (!checkQuayId(docData)) return false;
    const quay = quayMap[`${docData.quay_id}`];
    return (
      Object.hasOwn(docData, "berth_id") &&
      isNumber(docData.berth_id) &&
      docData.berth_id <= quay.max_berth
    );
  };
  return checkPortId(docData) && checkQuayId(docData) && checkBerthId(docData); // 全て有効で無い場合はfalse
};
